import { COLS, ROWS, Frame, HexColor } from './led-board.model';

export type FitMode = 'cover' | 'fit' | 'stretch';

export interface DrawRect { dx: number; dy: number; dw: number; dh: number; }

export function fitRect(
  mode: FitMode,
  imgW: number,
  imgH: number,
  offsetX = 0,
  offsetY = 0,
): DrawRect {
  if (mode === 'stretch' || imgW <= 0 || imgH <= 0) {
    return { dx: 0, dy: 0, dw: COLS, dh: ROWS };
  }
  const scale = mode === 'cover'
    ? Math.max(COLS / imgW, ROWS / imgH)
    : Math.min(COLS / imgW, ROWS / imgH);
  const dw = imgW * scale;
  const dh = imgH * scale;
  const ox = Math.max(-1, Math.min(1, offsetX));
  const oy = Math.max(-1, Math.min(1, offsetY));
  const dx = (COLS - dw) / 2 + (ox * (dw - COLS)) / 2;
  const dy = (ROWS - dh) / 2 + (oy * (dh - ROWS)) / 2;
  return { dx, dy, dw, dh };
}

function toHex2(n: number): string {
  const h = (n & 0xff).toString(16);
  return h.length < 2 ? '0' + h : h;
}

export function adjustFrame(
  frame: Frame,
  adj: { brightness: number; contrast: number; saturation: number },
): Frame {
  const bAdd = adj.brightness * 2.55;
  const cScaled = adj.contrast * 2.55;
  const cFactor = (259 * (cScaled + 255)) / (255 * (259 - cScaled));
  const sat = 1 + adj.saturation / 100;
  const clamp = (n: number) => (n < 0 ? 0 : n > 255 ? 255 : Math.round(n));
  const out: HexColor[] = new Array(frame.length);
  for (let i = 0; i < frame.length; i++) {
    const hex = frame[i];
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    // brightness
    r += bAdd; g += bAdd; b += bAdd;
    // contrast (around mid-gray 128)
    r = cFactor * (r - 128) + 128;
    g = cFactor * (g - 128) + 128;
    b = cFactor * (b - 128) + 128;
    // saturation (toward luminance)
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    r = lum + (r - lum) * sat;
    g = lum + (g - lum) * sat;
    b = lum + (b - lum) * sat;
    out[i] = '#' + toHex2(clamp(r)) + toHex2(clamp(g)) + toHex2(clamp(b));
  }
  return out;
}

export function rgbaToFrame(data: Uint8ClampedArray): Frame {
  const out: HexColor[] = new Array(COLS * ROWS);
  for (let i = 0; i < COLS * ROWS; i++) {
    out[i] = '#' + toHex2(data[i * 4]) + toHex2(data[i * 4 + 1]) + toHex2(data[i * 4 + 2]);
  }
  return out;
}
